import {
  AuctionResponse,
  LotStatus,
  PersonResponse,
  SaleStatus,
} from '@bonhams1793/bonhams-typescript'
import getConfig from 'next/config'
import { useEffect, useRef, useState } from 'react'

import { LotResponse } from '@nx/api'
import {
  useGetConditionsReportDocument,
  useGetConditionsReportParams,
  useGetConditionsReportRetry,
} from '@nx/api/hooks'
import { NewsIcon } from '@nx/fire/assets'
import { showToast } from '@nx/fire/toast'
import { getTranslate } from '@nx/translations'
import { useDataLayer } from '@nx/utils/google-tag-manager'

import { useModals } from '@web/components/common/ModalManager/ModalContext'
import { useGetUser } from '@web/helpers/api/user'

import { ActionPanelItem } from '../ActionPanel/ActionPanelItem'
import translate from './DownloadConditionsReport.translate.json'

const { publicRuntimeConfig } = getConfig()
const { text } = getTranslate(translate)

export function DownloadConditionsReport({
  auction,
  lot,
  contacts,
  displayConditionReportIcon = true,
}: DownloadConditionsReportProps) {
  const { data: user } = useGetUser()
  const dataLayer = useDataLayer()
  const { dispatch } = useModals()

  const [getConditionsReportEnabled, setGetConditionsReportEnabled] =
    useState(false)
  const downloadButtonRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)

  const conditionsReportParams = useGetConditionsReportParams(
    lot.iSaleLotNoUnique,
    publicRuntimeConfig.WEB_API_URL,
    Boolean(getConditionsReportEnabled && !!user)
  )

  const conditionsReportRetry = useGetConditionsReportRetry(
    lot.iSaleLotNoUnique,
    publicRuntimeConfig.WEB_API_URL,
    conditionsReportParams.data
  )

  const conditionsReportDocument = useGetConditionsReportDocument(
    lot.iSaleLotNoUnique,
    publicRuntimeConfig.WEB_API_URL,
    conditionsReportRetry.data
  )

  const isIdle =
    conditionsReportParams.fetchStatus === 'idle' ||
    conditionsReportRetry.fetchStatus === 'idle' ||
    // istanbul ignore next
    conditionsReportDocument.fetchStatus === 'idle'

  const isLoading =
    getConditionsReportEnabled &&
    (conditionsReportParams.fetchStatus === 'fetching' ||
      conditionsReportRetry.fetchStatus === 'fetching' ||
      conditionsReportDocument.fetchStatus === 'fetching')

  const hasFailed =
    conditionsReportParams.status === 'error' ||
    conditionsReportRetry.status === 'error' ||
    conditionsReportDocument.status === 'error'

  const hasSucceeded =
    getConditionsReportEnabled && conditionsReportDocument.status === 'success'

  useEffect(() => {
    if (hasSucceeded) {
      downloadButtonRef.current?.click()
    }

    return () => {
      setGetConditionsReportEnabled(false)
    }
  }, [hasSucceeded])

  useEffect(() => {
    if (hasFailed) {
      showToast.error(text('downloadFailed'))
    }
  }, [hasFailed])

  function handleClick(e: React.SyntheticEvent) {
    if (!hasSucceeded) e.preventDefault()

    if (isIdle) {
      if (!user) {
        dispatch?.({
          type: 'OPEN',
          payload: {
            modal: 'LOGIN',
            props: {
              brand: auction.brand,
              user,
            },
          },
        })
      } else {
        dataLayer.push({
          event: 'conditionReport',
          conditionReportData: {
            lotid: `${auction.iSaleNo}-${lot.iSaleLotNo}`,
            userid: `${user.customerId}`,
          },
        })
        setGetConditionsReportEnabled(true)
      }
    }

    if (hasFailed) {
      conditionsReportParams.refetch()
    }
  }

  if (lot.iSaleLotNoUnique && lot.has_condition_report) {
    let url: string | undefined
    let download: string | undefined
    if (hasSucceeded && conditionsReportDocument.data) {
      url = conditionsReportDocument.data.documentUrl
      download = conditionsReportDocument.data.filename
    } else {
      url = undefined
      download = undefined
    }

    return (
      <ActionPanelItem
        ref={downloadButtonRef}
        href={url}
        download={download}
        onClick={handleClick}
        icon={displayConditionReportIcon ? NewsIcon : undefined}
        isLoading={isLoading}
        title={text('viewReport')}
      />
    )
  } else if (
    auction.lOfferCondition &&
    auction.sSaleStatus !== SaleStatus.finished &&
    lot.sLotStatus !== LotStatus.withdrawn
  ) {
    const createHref = () => {
      const lotId = `${lot.iSaleLotNo}${lot?.sSaleLotNoA}`
      const emailSubject = text('email.subject', {
        lotId,
        auctionId: `${auction.iSaleNo}`,
        description: `${lot.sDesc.substring(0, 50)}${
          lot.sDesc.length > 50 ? '...' : ''
        }`,
      })
      const bodyText = text('email.body', {
        lotId,
        auctionId: `${auction.iSaleNo}`,
        description: lot.sDesc,
      })

      return `mailto: ${
        contacts && contacts.length > 0 && contacts[0].contact.email
      }?subject=${emailSubject}&body=${bodyText}`
    }

    return (
      <ActionPanelItem
        href={createHref()}
        target="_blank"
        rel="noreferrer"
        icon={displayConditionReportIcon ? NewsIcon : undefined}
        title={text('requestReport')}
      />
    )
  }

  return null
}

export interface DownloadConditionsReportProps {
  auction: AuctionResponse
  lot: LotResponse
  contacts: PersonResponse[]
  displayConditionReportIcon?: boolean
}
